import { createContext, useEffect, useState } from "react";

import type { Dispatch, FC, ReactNode, SetStateAction } from "react";

export type DisplayTheme = "light" | "dark";

interface Settings {
  theme: DisplayTheme;
  openAccordions: boolean;
  expandListView: boolean;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: Dispatch<SetStateAction<Settings>>;
  set?: <T extends keyof Settings>(key: T, value: Settings[T]) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  theme: "light",
  openAccordions: false,
  expandListView: false,
};

const restoreSettings = (): Settings => {
  let settings = initialSettings;

  try {
    const storedData = globalThis.localStorage.getItem("settings");

    if (storedData) {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      settings = JSON.parse(storedData) as Settings;
    } else {
      settings = {
        theme: globalThis.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
        openAccordions: false,
        expandListView: false,
      };
    }
  } catch (error) {
    console.error(error);
    // If stored data is not a stringified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

const storeSettings = (settings: Settings): void => {
  globalThis.localStorage.setItem("settings", JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => undefined,
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(restoreSettings());

  useEffect(() => {
    storeSettings(settings);
  }, [settings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings: setSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;
